import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  X, Check, Pencil, MoreHorizontal, Archive, Copy, Trash2, Loader2,
  Calendar, Clock, Folder, Layout, Columns, Tag, Bell, Repeat, Timer,
  User, Pause, Ban, AlignLeft,
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/tasks/ConfirmDialog";
import { SubtaskList } from "@/components/tasks/SubtaskList";
import { fmtDue, fmtTimeOfDay, fmtDur } from "@/lib/taskProperties";
import { AppIcon } from "@/lib/appearanceIcons";

const uid = () => Math.random().toString(36).slice(2, 10);
// Ensure every subtask carries a stable id (self-heals legacy rows on first save).
const normalize = (subs) => (subs || []).map((s) => ({ ...s, id: s.id || s.sid || uid() }));

// ─── Canonical View Task surface ────────────────────────────────────────────
// Opened on ordinary task/card click. Read-only presentation of the full task;
// the ONLY mutating controls are the isolated parent-completion checkbox and the
// canonical SubtaskList toggles. "Edit Task" hands off to the Edit drawer.
export function TaskView({ taskId, cats = [], boards = [], members = [], canAssign = false, onClose, onEdit, onChanged }) {
  const [t, setT] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    apiFetch(`/tasks/${taskId}`)
      .then((d) => {
        const norm = normalize(d.subtasks);
        setT(d); setSubs(norm);
        // Self-heal: persist stable ids for any legacy subtask that lacked one.
        if ((d.subtasks || []).some((s) => !(s.id || s.sid)) && norm.length) {
          apiFetch(`/tasks/${taskId}`, { method: "PATCH", body: JSON.stringify({ subtasks: norm }) }).catch(() => {});
        }
      })
      .catch((e) => toast.error("Could not load task", { description: e.message }))
      .finally(() => setLoading(false));
  }, [taskId]);

  // Never leave the body locked after unmount.
  useEffect(() => () => {
    document.body.style.pointerEvents = "";
    document.body.style.overflow = "";
    document.body.removeAttribute("data-scroll-locked");
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggleSub = async (id) => {
    const prev = subs;
    const next = subs.map((s) => (s.id === id ? { ...s, done: !s.done } : s));
    setSubs(next);
    setPending((p) => new Set(p).add(id));
    try { await apiFetch(`/tasks/${taskId}`, { method: "PATCH", body: JSON.stringify({ subtasks: next }) }); onChanged?.(); }
    catch (e) { setSubs(prev); toast.error("Couldn't update subtask", { description: e.message }); }
    finally { setPending((p) => { const n = new Set(p); n.delete(id); return n; }); }
  };

  const done = t?.status === "done";
  const toggleComplete = async () => {
    try {
      await apiFetch(`/tasks/${taskId}/${done ? "reopen" : "complete"}`, { method: "PUT" });
      setT((x) => ({ ...x, status: done ? "open" : "done" }));
      onChanged?.();
      if (!done) toast.success("Task completed");
    } catch (e) { toast.error(e.message); }
  };

  const doArchive = async () => { setBusy(true); try { await apiFetch(`/tasks/${taskId}/archive`, { method: "PUT", body: JSON.stringify({ archived: true }) }); toast.success("Task archived"); onChanged?.(); setConfirm(null); setTimeout(onClose, 0); } catch (e) { toast.error("Could not archive task", { description: e.message }); } finally { setBusy(false); } };
  const doDelete = async () => { setBusy(true); try { await apiFetch(`/tasks/${taskId}`, { method: "DELETE" }); toast.success("Task deleted"); onChanged?.(); setConfirm(null); setTimeout(onClose, 0); } catch (e) { toast.error("Could not delete task", { description: e.message }); } finally { setBusy(false); } };
  const doDuplicate = async () => { try { await apiFetch(`/tasks/${taskId}/duplicate`, { method: "POST" }); toast.success("Task duplicated"); onChanged?.(); } catch (e) { toast.error("Could not duplicate task", { description: e.message }); } };

  const canHardDelete = t && ["personal", "reminder"].includes(t.task_type) && !t.related_id;
  const assignee = members.find((m) => m.id === t?.owner_id);
  const catObj = cats.find((c) => c.id === t?.category_id);
  const catName = catObj?.name;
  const catColor = catObj?.color;
  const tracked = t?.tracked_seconds || 0;
  const est = (t?.estimate_minutes || 0) * 60;
  const dueMeta = t?.due_date ? fmtDue(t.due_date) : null;
  const boardObj = boards.find((b) => b.id === t?.board_id);
  const boardName = boardObj?.name || t?.board_name || "";
  const stageName = boardObj?.stages?.find((s) => s.id === t?.stage_id)?.name || t?.stage_name || t?.stage || "";
  const stageObj = boardObj?.stages?.find((s) => s.id === t?.stage_id);

  return createPortal(
    <div className="fixed inset-0 z-drawer" data-testid="task-view">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-200" onClick={onClose} />
      <aside className="absolute inset-y-0 right-0 flex w-full flex-col border-l border-border bg-card shadow-lift animate-in slide-in-from-right duration-300 sm:w-[560px] lg:w-[600px] max-sm:inset-0 max-sm:border-l-0">
        {loading || !t ? (
          <div className="flex flex-1 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-accent" /></div>
        ) : (
          <>
            {/* HEADER */}
            <header className="flex h-16 shrink-0 items-center justify-between border-b border-border bg-card/95 px-5 backdrop-blur-md">
              <div className="flex min-w-0 items-center gap-3">
                <button onClick={toggleComplete} data-testid="view-complete"
                  aria-label={done ? "Reopen task" : "Complete task"}
                  className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors", done ? "border-success bg-success text-white" : "border-border hover:border-accent hover:bg-accent-subtle")}>
                  <Check className={cn("h-3.5 w-3.5", !done && "opacity-0")} />
                </button>
                <div className="flex min-w-0 flex-col leading-tight">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t.status_lifecycle === "archived" ? "Archived" : done ? "Completed" : "View task"}</span>
                  {t.related_id && <span className="truncate text-[11px] text-muted-foreground/70">Linked · {t.related_type || "workflow"}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Button size="sm" onClick={onEdit} className="gap-1.5" data-testid="view-edit"><Pencil className="h-3.5 w-3.5" /> Edit Task</Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><button className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="view-more"><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => setConfirm("archive")} data-testid="view-action-archive"><Archive className="mr-2 h-4 w-4" /> Archive task</DropdownMenuItem>
                    <DropdownMenuItem onClick={doDuplicate} data-testid="view-action-duplicate"><Copy className="mr-2 h-4 w-4" /> Duplicate</DropdownMenuItem>
                    {canHardDelete && <><DropdownMenuSeparator /><DropdownMenuItem onClick={() => setConfirm("delete")} className="text-destructive focus:text-destructive" data-testid="view-action-delete"><Trash2 className="mr-2 h-4 w-4" /> Delete task</DropdownMenuItem></>}
                  </DropdownMenuContent>
                </DropdownMenu>
                <button onClick={onClose} data-testid="view-close" aria-label="Close" className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"><X className="h-5 w-5" /></button>
              </div>
            </header>

            {/* BODY */}
            <div className="flex-1 space-y-6 overflow-y-auto scrollbar-thin p-6">
              <h1 data-testid="view-title" className={cn("font-heading text-2xl font-semibold leading-tight text-foreground", done && "text-muted-foreground line-through")}>{t.title}</h1>

              {t.description && (
                <div className="flex gap-3" data-testid="view-description">
                  <AlignLeft className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-foreground/90">{t.description}</p>
                </div>
              )}

              {/* Meta grid */}
              <div className="grid grid-cols-1 gap-x-6 gap-y-4 rounded-xl border border-border/70 bg-muted/20 p-4 sm:grid-cols-2">
                <MetaRow icon={Calendar} label="Due date" testid="view-due">
                  {dueMeta ? <span className={cn(dueMeta.tone === "destructive" && "text-destructive", dueMeta.tone === "accent" && "text-accent")}>{dueMeta.text}</span> : <Muted />}
                </MetaRow>
                <MetaRow icon={Clock} label="Due time" testid="view-duetime">{t.due_time ? fmtTimeOfDay(t.due_time) : <Muted />}</MetaRow>
                <MetaRow icon={Timer} label="Priority" testid="view-priority"><span className="capitalize">{t.priority || "normal"}</span></MetaRow>
                {canAssign && (
                  <MetaRow icon={User} label="Assignee" testid="view-assignee">{assignee ? (assignee.full_name || assignee.name || assignee.login_email) : <span className="text-muted-foreground">Myself</span>}</MetaRow>
                )}
                <MetaRow icon={Folder} label="Category" testid="view-category">
                  {catName ? <span className="inline-flex items-center gap-1.5">{catObj?.icon ? <AppIcon name={catObj.icon} className="h-3.5 w-3.5" style={catColor ? { color: catColor } : undefined} /> : <span className="h-2.5 w-2.5 rounded-full" style={catColor ? { backgroundColor: catColor } : undefined} />}{catName}</span> : <Muted />}
                </MetaRow>
                <MetaRow icon={Layout} label="Board" testid="view-board">{boardName || <Muted />}</MetaRow>
                {t.board_id && <MetaRow icon={Columns} label="Stage" testid="view-stage">{stageName ? <span className="inline-flex items-center gap-1.5" style={stageObj?.colour ? { color: stageObj.colour } : undefined}>{stageObj?.icon && <AppIcon name={stageObj.icon} className="h-3.5 w-3.5" />}{stageName}</span> : <Muted />}</MetaRow>}
                <MetaRow icon={Bell} label="Reminder" testid="view-reminder">{t.reminders?.length ? fmtTimeOfDay(String(t.reminders[0].at).slice(11, 16)) || "Set" : <Muted />}</MetaRow>
                <MetaRow icon={Repeat} label="Repeat" testid="view-recurrence">{t.recurrence_summary || <Muted />}</MetaRow>
                <MetaRow icon={Timer} label="Tracked time" testid="view-time">{tracked ? `${fmtDur(tracked)}${est ? ` / ${fmtDur(est)}` : ""}` : est ? `Est ${fmtDur(est)}` : <Muted />}</MetaRow>
                {(t.waiting || t.blocked_reason) && (
                  <MetaRow icon={t.blocked_reason ? Ban : Pause} label="Status" testid="view-waiting">
                    <span className={t.blocked_reason ? "text-destructive" : "text-warning"}>{t.blocked_reason ? "Blocked" : "Waiting"}</span>
                  </MetaRow>
                )}
              </div>

              {/* Tags */}
              {t.tags?.length > 0 && (
                <div className="flex flex-wrap items-center gap-2" data-testid="view-tags">
                  {t.tags.map((tg) => (
                    <span key={tg} className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-[12px] font-medium text-muted-foreground">
                      <Tag className="h-3 w-3" /> {tg}
                    </span>
                  ))}
                </div>
              )}

              {/* Subtasks — canonical control (toggle only in View) */}
              {subs.length > 0 && (
                <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
                  <SubtaskList items={subs} onToggle={toggleSub} pending={pending} testid="view-subtask" />
                </div>
              )}
            </div>
          </>
        )}
      </aside>

      <ConfirmDialog open={confirm === "archive"} onOpenChange={(o) => !o && setConfirm(null)} testid="view-archive-dialog" busy={busy} title="Archive this task?" description="This task will be removed from active views. You can restore it later." confirmLabel="Archive Task" onConfirm={doArchive} />
      <ConfirmDialog open={confirm === "delete"} onOpenChange={(o) => !o && setConfirm(null)} testid="view-delete-dialog" busy={busy} destructive title="Delete this task permanently?" description="This cannot be undone." confirmLabel="Delete Permanently" onConfirm={doDelete} />
    </div>,
    document.body
  );
}

function MetaRow({ icon: Icon, label, children, testid }) {
  return (
    <div className="flex items-start gap-2.5" data-testid={testid}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <div className="text-[12px] font-medium uppercase tracking-wide text-muted-foreground/80">{label}</div>
        <div className="mt-0.5 text-[15px] text-foreground">{children}</div>
      </div>
    </div>
  );
}
const Muted = () => <span className="text-muted-foreground/50">—</span>;
